import axios from 'axios';
import dayjs from 'dayjs';

import { trackError, ErrorCategory, ErrorSeverity } from 'utils/analytics';
import { config as env } from 'utils/config/env';
import { Logger } from 'utils/logger';

import { useLoadingStore, useTokenStore } from 'store/index';
import { useWorkspaceStore } from 'store/workspace.store';

/**
 * Generates user-friendly loading messages based on API endpoint URLs.
 * Maps endpoint patterns to descriptive titles and descriptions for the loading overlay.
 *
 * @param url - The API endpoint URL to generate a message for
 * @returns Object with title and description for the loading state
 */
const getLoadingMessage = (url: string | undefined) => {
  if (!url) return { title: 'Loading', description: 'Please wait...' };

  const parts = url.split('/').filter(Boolean);
  const endpoint = parts[parts.length - 1] || '';

  const messageMap: Record<string, { title: string; description: string }> = {
    organisations: {
      title: 'Loading Organizations',
      description: 'One moment while we load your organizations...',
    },
    'access-control/user_access': {
      title: 'Loading User Access',
      description: 'Taking a look at your permissions...',
    },
  };

  if (messageMap[endpoint]) {
    return messageMap[endpoint];
  }

  for (const key in messageMap) {
    if (url?.toLowerCase().includes(key.toLowerCase())) {
      return messageMap[key];
    }
  }

  // If no message is found, do not show a loader
  return null;
};

const api = axios.create({
  baseURL: env.VITE_API_URL,
});

api.interceptors.request.use(
  (config) => {
    let loadingRequestId: string | undefined;

    // Only show loader if skipLoader is not set (initial fetches, not background refetches)
    if (!config.skipLoader) {
      const message = getLoadingMessage(config.url);
      if (message) {
        const { title, description } = message;
        loadingRequestId = useLoadingStore
          .getState()
          .startLoading(title, description);
      }
    }

    const accessToken = useTokenStore.getState().accessToken;
    const workspaceId = useWorkspaceStore.getState().currentWorkspaceId;

    const headers = config.headers || {};

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    if (workspaceId) {
      headers['X-Organization-ID'] = workspaceId;
    }

    const modifiedConfig = {
      ...config,
      headers,
      metadata: {
        ...config.metadata,
        loadingRequestId,
      },
    };

    return modifiedConfig;
  },
  (error) => {
    const requestId = error.config?.metadata?.loadingRequestId;
    useLoadingStore.getState().stopLoading(requestId);
    throw new Error(error.message);
  }
);

api.interceptors.response.use(
  (response) => {
    const requestId = response.config?.metadata?.loadingRequestId;
    useLoadingStore.getState().stopLoading(requestId);
    return response;
  },
  async (error) => {
    const requestId = error.config?.metadata?.loadingRequestId;
    useLoadingStore.getState().stopLoading(requestId);

    const errorContext = {
      endpoint: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      baseURL: error.config?.baseURL,
      params: error.config?.params,
      responseStatus: error.response?.status,
      responseStatusText: error.response?.statusText,
      responseData: error.response?.data,
      requestId: error.response?.headers?.['x-request-id'],
      requestDuration: error.config?.metadata?.requestStartedAt
        ? dayjs().valueOf() - error.config.metadata.requestStartedAt
        : undefined,
      code: error.code,
    };

    let category = ErrorCategory.API;
    let severity = ErrorSeverity.MEDIUM;

    if (error.response?.status === 401 || error.response?.status === 403) {
      category = ErrorCategory.AUTHENTICATION;
      severity = ErrorSeverity.HIGH;
    } else if (error.response?.status === 404) {
      category = ErrorCategory.API;
      severity = ErrorSeverity.MEDIUM;
    } else if (error.response?.status === 429) {
      category = ErrorCategory.API;
      severity = ErrorSeverity.HIGH;
    } else if (error.response?.status >= 500) {
      category = ErrorCategory.API;
      severity = ErrorSeverity.CRITICAL;
    } else if (
      error.code === 'ECONNABORTED' ||
      error.message?.toLowerCase().includes('timeout')
    ) {
      category = ErrorCategory.NETWORK;
      severity = ErrorSeverity.HIGH;
    } else if (error.message === 'Network Error') {
      category = ErrorCategory.NETWORK;
      severity = ErrorSeverity.CRITICAL;
    }

    trackError({
      error,
      context: errorContext,
      category,
      severity,
    });

    const originalRequest = error.config;

    if (error.response?.status !== 401 || originalRequest._retry) {
      if (error.message === 'Network Error' && error.request?.status === 0) {
        // Most probably a CORS issue, which an end user cannot fix.  Log error
        // and log them out
        Logger.error(`URL: ${error.config.url} was blocked by CORS`);

        throw new Error(
          'Unable to connect. Please check your internet connection and try again. If the problem persists, please contact support.'
        );
      }

      if (
        error.code === 'ECONNABORTED' ||
        error.message?.toLowerCase().includes('timeout')
      ) {
        throw new Error(
          'Request timed out. Please check your internet connection and try again.'
        );
      }

      throw error;
    }
  }
);

export default api;
